// Ads: spend / reach / CPM / CTR ตามช่วงของ Biddable
import { useQuery } from "@tanstack/react-query";
import type { Brand } from "@contracts/api";
import { useCampaigns } from "@features/campaigns/api/use-campaigns";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { brandCfgLink } from "@features/navigation/model/side-menus";
import { fmtBaht, fmtCompact } from "@shared/lib/format";
import { biddableMetrics, fetchBiddableScope, overviewKeys } from "../api/overview-api";
import { OvKpi } from "../components/ov-kpi";
import { OvLoading, OvNote } from "../components/ov-states";

export function AdsBlock({ brand, preset }: { brand: Brand; preset: string }) {
  const { data: campaigns, isPending: loadingCampaigns } = useCampaigns(brand.id);
  const links = (campaigns ?? []).flatMap((c) => (c.links ?? []).filter((l) => l.kind === "ads"));
  const hasToken = !!wazzupToken();

  const { data: accounts, isPending } = useQuery({
    queryKey: overviewKeys.ads(preset),
    queryFn: () => fetchBiddableScope(preset),
    enabled: hasToken && links.length > 0,
  });

  if (loadingCampaigns) return <OvLoading />;
  if (!links.length)
    return (
      <OvNote>
        {(campaigns ?? []).length ? (
          <>
            ยังไม่ได้ผูก Ad Account — ผูกได้ที่{" "}
            <a href={brandCfgLink(brand)}>หน้าตั้งค่า → Campaigns</a>
          </>
        ) : (
          "ยังไม่มีแคมเปญ"
        )}
      </OvNote>
    );
  if (hasToken && isPending) return <OvLoading />;

  let spend = 0;
  let reach = 0;
  let impressions = 0;
  let clicks = 0;
  for (const l of links) {
    const m = biddableMetrics(accounts, l.meta);
    if (!m) continue;
    spend += m.spend ?? 0;
    reach += m.reach ?? 0;
    impressions += m.impressions ?? 0;
    clicks += m.clicks ?? 0;
  }
  const cpm = impressions ? (spend / impressions) * 1000 : null;
  const ctr = impressions ? clicks / impressions : null;

  return (
    <div className="ov-kpis">
      <OvKpi
        label="Spend"
        value={accounts ? fmtBaht(spend) : "—"}
        sub={accounts ? `${links.length} บัญชีที่ผูกไว้` : "ต้องเข้าสู่ระบบก่อน"}
      />
      <OvKpi
        label="Reach"
        value={accounts ? fmtCompact(reach) : "—"}
        sub={accounts ? `${fmtCompact(impressions)} impressions` : ""}
      />
      <OvKpi
        label="CPM"
        value={cpm == null ? "—" : fmtBaht(cpm)}
        sub="ต้นทุนต่อการเข้าถึงพันครั้ง"
      />
      <OvKpi
        label="CTR"
        value={ctr == null ? "—" : (ctr * 100).toFixed(2) + "%"}
        sub={accounts ? `${fmtCompact(clicks)} clicks` : ""}
      />
    </div>
  );
}
